import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import {
  RiskManagementOverallRiskBarChartsDocument,
  type RiskManagementOverallRiskBarChartsQuery,
  type RiskManagementOverallRiskBarChartsQueryVariables,
} from "$/__generated__/graphql";

import {
  appendRiskManagementSubgroups,
  appendOverallRiskTooltipExtras,
  convertOverallBarDataToSeries,
  filterNullishOPINTotals,
  filterNullishThreatTotals,
} from "./utils";
import { useGlobalFiltersContext } from "contexts/filters/context";
import {
  GeneralBarDatum,
  storageFormatters,
} from "@jana-technology/platform-ui";

export const useOverallRiskCharts = (
  options: useQuery.Options<
    RiskManagementOverallRiskBarChartsQuery,
    RiskManagementOverallRiskBarChartsQueryVariables
  >,
) => {
  const overallRiskChartsQueryResult = useQuery(
    RiskManagementOverallRiskBarChartsDocument,
    options,
  );

  const {
    overallOperationalRiskByWellName,
    overallInterventionRiskByWellName,
    selectedWellName,
    appliedHierarchyName,
  } = useGlobalFiltersContext();

  const {
    overallRiskByThreatSeries,
    overallRiskByConsequenceSeries,
    overallRiskByOutcomeScenarioSeries,
    overallRiskDataKeys,
  } = useMemo(() => {
    let overallRiskByThreatSeries: Array<GeneralBarDatum> = [];
    let overallRiskByConsequenceSeries: Array<GeneralBarDatum> = [];
    let overallRiskByOutcomeScenarioSeries: Array<GeneralBarDatum> = [];

    if (overallRiskChartsQueryResult.data?.riskManagementChartsForWellNode) {
      const {
        overallRiskByThreat,
        overallRiskByConsequence,
        overallRiskByOutcomeScenario,
      } = overallRiskChartsQueryResult.data.riskManagementChartsForWellNode;

      overallRiskByThreatSeries = convertOverallBarDataToSeries(
        filterNullishThreatTotals(overallRiskByThreat),
      );

      overallRiskByConsequenceSeries = convertOverallBarDataToSeries(
        filterNullishOPINTotals(overallRiskByConsequence),
      );

      overallRiskByOutcomeScenarioSeries = convertOverallBarDataToSeries(
        filterNullishOPINTotals(overallRiskByOutcomeScenario),
      );

      const tooltipTitle = selectedWellName?.label
        ? `${selectedWellName.label} (${appliedHierarchyName})`
        : "";

      for (const threatDatum of overallRiskByThreatSeries) {
        threatDatum.title = tooltipTitle;
        threatDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        appendOverallRiskTooltipExtras(
          overallOperationalRiskByWellName,
          overallInterventionRiskByWellName,
          threatDatum,
        );
      }

      for (let i = 0; i < overallRiskByConsequenceSeries.length; i++) {
        overallRiskByConsequenceSeries[i].title = tooltipTitle;
        overallRiskByConsequenceSeries[i].formatter = (value: number) =>
          storageFormatters.currency(value);
        appendRiskManagementSubgroups(
          [overallRiskByConsequence[i]],
          overallRiskByConsequenceSeries[i],
          "By Category",
        );

        appendOverallRiskTooltipExtras(
          overallOperationalRiskByWellName,
          overallInterventionRiskByWellName,
          overallRiskByConsequenceSeries[i],
        );
      }

      for (let i = 0; i < overallRiskByOutcomeScenarioSeries.length; i++) {
        overallRiskByOutcomeScenarioSeries[i].title = tooltipTitle;
        overallRiskByOutcomeScenarioSeries[i].formatter = (value: number) =>
          storageFormatters.currency(value);
        overallRiskByOutcomeScenarioSeries[i].tooltipExtras = [
          {
            label: `LOF (${overallRiskByOutcomeScenario[i].group})`,
            value: storageFormatters.scientificNotation(
              overallRiskByOutcomeScenario[i]?.opLof ?? 0,
            ),
            subGroup: "Operational Risk",
          },
          {
            label: `LOF (${overallRiskByOutcomeScenario[i].group})`,
            value: storageFormatters.scientificNotation(
              overallRiskByOutcomeScenario[i]?.inLof ?? 0,
            ),
            subGroup: "Intervention Risk",
          },
          {
            label: `COF (${overallRiskByOutcomeScenario[i].group})`,
            value: storageFormatters.currency(
              overallRiskByOutcomeScenario[i]?.opCof ?? 0,
            ),
            subGroup: "Operational Risk",
          },
          {
            label: `COF (${overallRiskByOutcomeScenario[i].group})`,
            value: storageFormatters.currency(
              overallRiskByOutcomeScenario[i]?.inCof ?? 0,
            ),
            subGroup: "Intervention Risk",
          },
        ];

        appendOverallRiskTooltipExtras(
          overallOperationalRiskByWellName,
          overallInterventionRiskByWellName,
          overallRiskByOutcomeScenarioSeries[i],
        );
      }

      return {
        overallRiskByThreatSeries,
        overallRiskByConsequenceSeries,
        overallRiskByOutcomeScenarioSeries,
        overallRiskDataKeys: ["Operational Risk", "Intervention Risk"],
      };
    }

    return {
      overallRiskByThreatSeries: [],
      overallRiskByConsequenceSeries: [],
      overallRiskByOutcomeScenarioSeries: [],
      overallRiskDataKeys: [],
    };
  }, [
    overallRiskChartsQueryResult.data?.riskManagementChartsForWellNode,
    overallOperationalRiskByWellName,
    overallInterventionRiskByWellName,
    selectedWellName,
    appliedHierarchyName,
  ]);

  return {
    overallRiskChartsQueryResult,
    overallRiskByThreatSeries,
    overallRiskByConsequenceSeries,
    overallRiskByOutcomeScenarioSeries,
    overallRiskDataKeys,
  };
};
